import i18n.StringKey
import i18n.tr
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.GlobalScope
import kotlinx.coroutines.launch
import org.w3c.dom.HTMLElement
import org.w3c.dom.HTMLInputElement

class ResetPasswordPage(
    private val onBackToLogin: () -> Unit
) {
    private val apiService = ApiService()

    /**
     * The page has two states driven by whether a `token=` query lands in the
     * URL the user opened from their email:
     *   - no token  → step 1, "request a reset link"  → POST /api/forgot-password
     *   - has token → step 2, "set a new password"   → POST /api/reset-password
     */
    fun render(): HTMLElement {
        val token = extractTokenFromUrl()
        return if (token.isNullOrBlank()) renderRequestStep() else renderConfirmStep(token)
    }

    private fun renderRequestStep(): HTMLElement {
        val container = baseContainer()
        container.innerHTML = """
            <div style="
                background: rgba(20, 20, 24, 0.9); border-radius: 16px; padding: 40px 32px;
                box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                width: 340px; max-width: 90vw;
            ">
                <h2 style="color: #fff; text-align: center; margin: 0 0 8px; font-size: 24px;">${StringKey.RESET_PASSWORD_TITLE.tr()}</h2>
                <p style="color: #666; text-align: center; font-size: 14px; margin-bottom: 24px;">
                    ${StringKey.RESET_PASSWORD_SUBTITLE.tr()}
                </p>

                <div style="margin-bottom: 16px;">
                    <input type="email" id="reset-email" placeholder="${StringKey.RESET_PASSWORD_EMAIL_PLACEHOLDER.tr()}" style="
                        width: 100%; padding: 14px 16px; border: 1px solid #333; border-radius: 8px;
                        font-size: 16px; box-sizing: border-box; background-color: #1a1a1e; color: #fff; outline: none;
                    ">
                </div>

                <div id="reset-message" style="display: none; margin-bottom: 16px; padding: 10px; border-radius: 6px; font-size: 14px; text-align: center;"></div>

                <button id="reset-submit-btn" style="
                    width: 100%; padding: 14px; background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                    color: #0a0a0a; border: none; border-radius: 8px; font-size: 16px; font-weight: 600;
                    cursor: pointer; box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3); margin-bottom: 16px;
                ">${StringKey.RESET_PASSWORD_SUBMIT.tr()}</button>

                <button id="reset-back-btn" style="
                    width: 100%; padding: 14px; background: transparent; color: #00d9ff;
                    border: 1px solid #333; border-radius: 8px; font-size: 16px; cursor: pointer;
                ">${StringKey.RESET_PASSWORD_BACK_TO_LOGIN.tr()}</button>
            </div>
        """

        container.querySelector("#reset-back-btn")?.addEventListener("click", { onBackToLogin() })
        container.querySelector("#reset-submit-btn")?.addEventListener("click", {
            val email = (container.querySelector("#reset-email") as? HTMLInputElement)?.value ?: ""
            val messageDiv = container.querySelector("#reset-message") as? HTMLElement
            if (email.isBlank()) {
                showMessage(messageDiv, StringKey.RESET_PASSWORD_ENTER_EMAIL.tr(), true)
            } else {
                GlobalScope.launch {
                    try {
                        apiService.forgotPassword(email)
                        // Server returns the same response whether or not the
                        // email matches an account, so we can't tell either —
                        // and that's the point. Always show a generic success.
                        showMessage(messageDiv, StringKey.RESET_PASSWORD_SUCCESS.tr(), false)
                    } catch (e: Exception) {
                        showMessage(messageDiv, StringKey.RESET_PASSWORD_FAILED.tr(), true)
                    }
                }
            }
        })

        return container
    }

    private fun renderConfirmStep(token: String): HTMLElement {
        val container = baseContainer()
        container.innerHTML = """
            <div style="
                background: rgba(20, 20, 24, 0.9); border-radius: 16px; padding: 40px 32px;
                box-shadow: 0 8px 32px rgba(0, 217, 255, 0.1), 0 0 0 1px rgba(255, 255, 255, 0.05);
                width: 340px; max-width: 90vw;
            ">
                <h2 style="color: #fff; text-align: center; margin: 0 0 8px; font-size: 24px;">${StringKey.RESET_PASSWORD_TITLE.tr()}</h2>
                <p style="color: #666; text-align: center; font-size: 14px; margin-bottom: 24px;">
                    ${StringKey.RESET_PASSWORD_SUBTITLE.tr()}
                </p>

                <div style="margin-bottom: 16px;">
                    <input type="password" id="reset-new-password" placeholder="${StringKey.RESET_PASSWORD_NEW_PASSWORD_PLACEHOLDER.tr()}" style="
                        width: 100%; padding: 14px 16px; border: 1px solid #333; border-radius: 8px;
                        font-size: 16px; box-sizing: border-box; background-color: #1a1a1e; color: #fff; outline: none;
                    ">
                </div>

                <div style="margin-bottom: 16px;">
                    <input type="password" id="reset-confirm-password" placeholder="${StringKey.RESET_PASSWORD_CONFIRM_PLACEHOLDER.tr()}" style="
                        width: 100%; padding: 14px 16px; border: 1px solid #333; border-radius: 8px;
                        font-size: 16px; box-sizing: border-box; background-color: #1a1a1e; color: #fff; outline: none;
                    ">
                </div>

                <div id="reset-message" style="display: none; margin-bottom: 16px; padding: 10px; border-radius: 6px; font-size: 14px; text-align: center;"></div>

                <button id="reset-submit-btn" style="
                    width: 100%; padding: 14px; background: linear-gradient(135deg, #00d9ff 0%, #00b8d9 100%);
                    color: #0a0a0a; border: none; border-radius: 8px; font-size: 16px; font-weight: 600;
                    cursor: pointer; box-shadow: 0 4px 12px rgba(0, 217, 255, 0.3); margin-bottom: 16px;
                ">${StringKey.RESET_PASSWORD_SUBMIT.tr()}</button>

                <button id="reset-back-btn" style="
                    width: 100%; padding: 14px; background: transparent; color: #00d9ff;
                    border: 1px solid #333; border-radius: 8px; font-size: 16px; cursor: pointer;
                ">${StringKey.RESET_PASSWORD_BACK_TO_LOGIN.tr()}</button>
            </div>
        """

        container.querySelector("#reset-back-btn")?.addEventListener("click", { onBackToLogin() })
        container.querySelector("#reset-submit-btn")?.addEventListener("click", {
            val newPassword = (container.querySelector("#reset-new-password") as? HTMLInputElement)?.value ?: ""
            val confirmPassword = (container.querySelector("#reset-confirm-password") as? HTMLInputElement)?.value ?: ""
            val messageDiv = container.querySelector("#reset-message") as? HTMLElement
            when {
                newPassword.length < 8 -> showMessage(messageDiv, StringKey.RESET_PASSWORD_MIN_LENGTH.tr(), true)
                newPassword != confirmPassword -> showMessage(messageDiv, StringKey.ERROR_PASSWORDS_DONT_MATCH.tr(), true)
                else -> {
                    GlobalScope.launch {
                        try {
                            val ok = apiService.resetPassword(token, newPassword)
                            if (ok) {
                                showMessage(messageDiv, StringKey.RESET_PASSWORD_SUCCESS.tr(), false)
                            } else {
                                showMessage(messageDiv, StringKey.RESET_PASSWORD_FAILED.tr(), true)
                            }
                        } catch (e: Exception) {
                            showMessage(messageDiv, StringKey.RESET_PASSWORD_FAILED.tr(), true)
                        }
                    }
                }
            }
        })

        return container
    }

    /** Looks for `token=...` anywhere in the URL — both `?token=` and `#...?token=`. */
    private fun extractTokenFromUrl(): String? {
        val parts = listOf(window.location.search, window.location.hash)
        for (part in parts) {
            val q = part.substringAfter('?', missingDelimiterValue = "")
            if (q.isEmpty()) continue
            for (pair in q.split('&')) {
                val (k, v) = pair.split('=', limit = 2).let {
                    if (it.size == 2) it[0] to it[1] else it[0] to ""
                }
                if (k == "token" && v.isNotBlank()) return v
            }
        }
        return null
    }

    private fun baseContainer(): HTMLElement {
        val container = document.createElement("div") as HTMLElement
        container.style.cssText = """
            display: flex; flex-direction: column; align-items: center; justify-content: center;
            min-height: 100vh; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
            background: linear-gradient(180deg, #0a0a0a 0%, #141418 100%); padding: 20px;
        """
        return container
    }

    private fun showMessage(div: HTMLElement?, message: String, isError: Boolean) {
        div ?: return
        div.style.display = "block"
        div.style.background = if (isError) "rgba(255, 107, 107, 0.1)" else "rgba(0, 217, 255, 0.1)"
        div.style.color = if (isError) "#ff6b6b" else "#00d9ff"
        div.textContent = message
    }
}
